import React, { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import type SignatureCanvas from 'react-signature-canvas';
import { TextField } from 'components/ui';
import { RequiredMark } from 'components/ui/FieldShell';
import { useFormLocked } from 'components/ui/FormLock';
import serverErrorHandler from 'services/serverError.service';
import clearSclaSignature from 'features/scla/api/clearSclaSignature';
import submitSclaApp from 'features/scla/api/submitSclaApp';
import updateSclaSignatureDetails from 'features/scla/api/updateSclaSignatureDetails';
import uploadSclaParentSignature from 'features/scla/api/uploadSclaParentSignature';
import uploadSclaStudentSignature from 'features/scla/api/uploadSclaStudentSignature';
import { getSclaAppHomePath, getSclaAppRecPath } from 'utils/navigation';
import SclaSignaturePad from '../components/steps/SclaSignaturePad';
import SclaStepMark from '../components/steps/SclaStepMark';
import { SCLA_APPLICATION_SECTIONS } from '../config/sections';
import { sclaStepState, type SclaStepId } from '../utils/sectionStatus';
import SclaStepCard from '../components/steps/SclaStepCard';
import styles from '../components/steps/SclaStep.module.scss';
import useAutosave, { useSectionReady } from '../hooks/useAutosave';
import useSclaSectionPath from '../hooks/useSclaSectionPath';
import { SclaApplicationOutletContext } from './SclaAppLayout';

function todayDateInput(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function signatureDateValue(value: string | null | undefined): string {
  const text = (value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (!match) return todayDateInput();
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

async function canvasFile(canvas: SignatureCanvas) {
  const blob = await (await fetch(canvas.toDataURL())).blob();
  const formData = new FormData();
  formData.append('file', blob, 'signature.png');
  return formData;
}

export default function SclaApplicationSignatures() {
  const { application, setApplication, readOnly } =
    useOutletContext<SclaApplicationOutletContext>();
  const signature = application.signatureInfo;
  const [studentFullName, setStudentFullName] = useState(signature.studentFullName || '');
  const [studentSignedDate, setStudentSignedDate] = useState(
    signatureDateValue(signature.studentSignedDate),
  );
  const [parentFullName, setParentFullName] = useState(signature.parentFullName || '');
  const [parentSignedDate, setParentSignedDate] = useState(
    signatureDateValue(signature.parentSignedDate),
  );
  const [parentIsAdult, setParentIsAdult] = useState(Boolean(signature.parentIsAdult));
  const [informationConfirmed, setInformationConfirmed] = useState(
    Boolean(application.informationConfirmed),
  );
  const ready = useSectionReady(application._id, () => {
    const next = application.signatureInfo;
    setStudentFullName(next.studentFullName || '');
    setStudentSignedDate(signatureDateValue(next.studentSignedDate));
    setParentFullName(next.parentFullName || '');
    setParentSignedDate(signatureDateValue(next.parentSignedDate));
    setParentIsAdult(Boolean(next.parentIsAdult));
    setInformationConfirmed(Boolean(application.informationConfirmed));
  });
  const [error, setError] = useState('');
  const sectionPath = useSclaSectionPath();
  const navigate = useNavigate();
  const requiredDone = SCLA_APPLICATION_SECTIONS
    .filter(section => (
      section.id !== 'rec'
      && section.id !== 'signature'
      && section.id !== 'transcripts'
    ))
    .every(section => section.getComplete(application));
  const signDisabled = readOnly || !requiredDone;
  const locked = useFormLocked();

  const save = async () => {
    if (locked) return false;
    const response = await updateSclaSignatureDetails(application._id, {
      studentFullName,
      studentSignedDate,
      parentFullName,
      parentSignedDate,
      parentIsAdult,
      informationConfirmed,
    });
    setApplication(response);
    return true;
  };

  const { flush } = useAutosave({
    serialized: JSON.stringify({
      studentFullName,
      studentSignedDate,
      parentFullName,
      parentSignedDate,
      parentIsAdult,
      informationConfirmed,
    }),
    enabled: !signDisabled && ready,
    save,
  });

  const uploadPad = async (who: 'student' | 'parent', canvas: SignatureCanvas) => {
    if (locked || signDisabled || canvas.isEmpty()) return;
    const formData = await canvasFile(canvas);
    const response = who === 'student'
      ? await uploadSclaStudentSignature(application._id, formData)
      : await uploadSclaParentSignature(application._id, formData);
    setApplication(response);
  };

  const clearSaved = async (who: 'student' | 'parent') => {
    if (locked) return;
    const file = who === 'student' ? signature.studentSignature : signature.parentSignature;
    if (!file || (!file.url && !file._id)) return;
    try {
      const updated = await clearSclaSignature(application._id, who);
      setApplication(updated);
    } catch (clearError) {
      serverErrorHandler(clearError);
      throw clearError;
    }
  };

  const signed = Boolean(signature.studentSignature && signature.parentSignature);
  const detailsDone = Boolean(
    studentFullName && studentSignedDate && parentFullName && parentSignedDate && parentIsAdult,
  );
  const canSubmit = requiredDone && signed && detailsDone && informationConfirmed;

  const handleSubmit = async () => {
    if (locked) return false;
    const posted = await flush();
    if (!canSubmit || !posted) {
      setError('Finish the required sections, both signatures, and the confirmation first.');
      return false;
    }
    try {
      await submitSclaApp(application._id);
      navigate(getSclaAppHomePath());
      return true;
    } catch (submitError) {
      serverErrorHandler(submitError);
      return false;
    }
  };

  return (
    <SclaStepCard
      title="Sign & submit"
      intro="Check every section is complete, then you and a parent or guardian sign."
      backTo={sectionPath(getSclaAppRecPath())}
      onContinue={handleSubmit}
      continueLabel="Submit application"
      continueDisabled={!canSubmit}
      continueTitle={canSubmit ? undefined : 'Finish the required sections first'}
      readOnly={readOnly}
    >
      <fieldset className={styles.group}>
        <legend>Review</legend>
        <ul className={styles.review}>
          {SCLA_APPLICATION_SECTIONS.slice(0, 6).map((section, index) => {
            const state = sclaStepState(application, section.id as SclaStepId);
            const open = state === 'started' || state === 'empty';
            return (
              <li key={section.id}>
                <SclaStepMark state={state} index={index} />
                <span className={styles.reviewName}>{section.navTitle}</span>
                {state === 'complete' ? <span className={styles.ok}>✓ Complete</span> : null}
                {state === 'optional' ? <span className={styles.hint}>Optional</span> : null}
                {open ? (
                  <Link className={styles.miss} to={sectionPath(section.to())}>Finish →</Link>
                ) : null}
              </li>
            );
          })}
        </ul>
      </fieldset>
      <fieldset className={styles.group} disabled={signDisabled}>
        <legend>Student signature</legend>
        <div className={styles.grid}>
          <TextField id="student-signer" label="Full name" required value={studentFullName} disabled={signDisabled} onChange={setStudentFullName} />
          <TextField id="student-date" label="Date" required type="date" value={studentSignedDate} disabled={signDisabled} onChange={setStudentSignedDate} />
        </div>
        {readOnly && signature.studentSignature?.url ? (
          <img src={signature.studentSignature.url} alt="Student signature" />
        ) : null}
        {readOnly && !signature.studentSignature?.url ? <p>No signature.</p> : null}
        {readOnly ? null : (
          <SclaSignaturePad
            id="student-signature"
            label="Student signature"
            disabled={signDisabled}
            onSigned={pad => { uploadPad('student', pad).catch(serverErrorHandler); }}
            onClear={() => clearSaved('student')}
          />
        )}
      </fieldset>
      <fieldset className={styles.group} disabled={signDisabled}>
        <legend>Parent or guardian signature</legend>
        <div className={styles.grid}>
          <TextField id="parent-signer" label="Full name" required value={parentFullName} disabled={signDisabled} onChange={setParentFullName} />
          <TextField id="parent-date" label="Date" required type="date" value={parentSignedDate} disabled={signDisabled} onChange={setParentSignedDate} />
        </div>
        <label className={styles.agree} htmlFor="parent-is-adult">
          <input id="parent-is-adult" type="checkbox" checked={parentIsAdult} disabled={signDisabled} onChange={event => setParentIsAdult(event.target.checked)} />
          I am the parent or legal guardian and I am 18 or older.
          <RequiredMark />
        </label>
        {readOnly && signature.parentSignature?.url ? (
          <img src={signature.parentSignature.url} alt="Parent signature" />
        ) : null}
        {readOnly && !signature.parentSignature?.url ? <p>No signature.</p> : null}
        {readOnly ? null : (
          <SclaSignaturePad
            id="parent-signature"
            label="Parent or guardian signature"
            disabled={signDisabled}
            onSigned={pad => { uploadPad('parent', pad).catch(serverErrorHandler); }}
            onClear={() => clearSaved('parent')}
          />
        )}
      </fieldset>
      <label className={styles.agree} htmlFor="information-confirmed">
        <input
          id="information-confirmed"
          type="checkbox"
          checked={informationConfirmed}
          disabled={signDisabled}
          onChange={event => setInformationConfirmed(event.target.checked)}
        />
        We confirm the information in this application is true and complete.
        <RequiredMark />
      </label>
      {error ? <p className={styles.fieldError} role="alert">{error}</p> : null}
    </SclaStepCard>
  );
}
